import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
  addLoadingAsset,
  addUnsupportedAsset,
  finishLoadingAsset,
  selectSelectedFolder,
  selectSupportedAudioTypes,
  selectSupportedImageTypes,
  selectSupportedPdfTypes,
  selectSupportedVideoTypes,
} from "../app/slices/AssetsSlice";
import { selectToken } from "../app/slices/AuthenticationSlice";
import { setHintDismissed } from "../app/slices/OnboardingSlice";
import { selectCurrentOrganization } from "../app/slices/UserSlice";
import { HintId } from "../features/onboarding/hints/hintIds";
import { AssetService } from "../services/AssetService";
import { useFileValidation } from "./FileValidationHook";
export const useNewAssetUpload = () => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const organization = useSelector(selectCurrentOrganization);
  const selectedFolder = useSelector(selectSelectedFolder);
  const supportedImageTypes = useSelector(selectSupportedImageTypes);
  const supportedVideoTypes = useSelector(selectSupportedVideoTypes);
  const supportedPdfTypes = useSelector(selectSupportedPdfTypes);
  const supportedAudioTypes = useSelector(selectSupportedAudioTypes);
  const supportedFilesTypes = [...supportedImageTypes, ...supportedVideoTypes, ...supportedPdfTypes, ...supportedAudioTypes];

  /**
   * Assets are stored per folder: AddNewAssetToFolder needs a folder id, so
   * with none selected there is nowhere to put the file.
   *
   * In practice this means an organisation with no folders at all, because
   * FetchFoldersHook selects the first folder as soon as any are fetched. The
   * upload affordances read this and refuse rather than accepting a file and
   * dropping it: uploadFile used to `return` on a missing folder, so picking
   * files with no folder did nothing at all, with no error and no clue why.
   */
  const canUpload = Boolean(organization && selectedFolder);

  const { IsVideoValid } = useFileValidation();
  const { t } = useTranslation();

  const handleFiles = async (files: FileList) => {
    // Backstop. The affordances are gated on canUpload, so reaching this with
    // no folder means a caller that has not been taught the rule yet.
    if (!canUpload) return;

    // The hint is onboarding for this author, not a statement about shared
    // folder contents. A colleague's files must not suppress it, while this
    // user's first real upload attempt completes and persists the lesson.
    if (files.length > 0) {
      dispatch(setHintDismissed(HintId.FileExplorerUpload));
    }

    for (let i = 0; i < files.length; i++) {
      let file: File = files[i];

      if (supportedFilesTypes.includes(file.type)) {
        console.log("file supported!");

        // TODO: check if file is video
        if (supportedVideoTypes.includes(file.type)) {
          if (await IsVideoValid(file)) {
            uploadFile(file);
          } else {
            dispatch(addUnsupportedAsset({ name: t("video_too_long"), id: uuidv4() }));
          }
        } else {
          uploadFile(file);
        }
      } else {
        console.log("file not supported");
        dispatch(addUnsupportedAsset({ name: file.name, id: uuidv4() }));
      }

      console.log(files[i].name + " " + files[i].type);
    }
  };

  async function uploadFile(file: File) {
    if (!organization || !selectedFolder) return;

    // add temp loading asset card
    dispatch(addLoadingAsset(file.name));
    var newAsset = await AssetService.AddNewAssetToFolder(organization.id, token, selectedFolder.id, file);
    if (newAsset) {
      console.log("fetch new asset");
      var updatedAsset = await AssetService.GetAsset(organization.id, token, newAsset?.id);
      console.log(updatedAsset);
      if (updatedAsset) {
        dispatch(finishLoadingAsset({ name: file.name, newAsset: updatedAsset }));
      }
    } else {
      // failed to add asset
    }
  }

  return [handleFiles, canUpload] as const;
};
